import { useState, useEffect, useCallback } from "react";
import {
  poolApi,
  parameterApi,
  type Pool,
  type PoolParameter,
} from "../services/poolService";
import {
  measurementApi,
  type Measurement,
} from "../services/measurementService";

export type ParameterStatus = "optimal" | "warning" | "critical";

export interface PoolParameterWithValue extends PoolParameter {
  currentValue: number | null;
  status: ParameterStatus;
}

interface UsePoolDataReturn {
  pools: Pool[];
  selectedPool: Pool | null;
  parameters: PoolParameterWithValue[];
  latestMeasurement: Measurement | null;
  loading: boolean;
  error: string | null;
  selectPool: (poolId: number) => void;
  refresh: () => void;
}

// Mapping between API parameter IDs and measurement reading IDs
// Backend now serves IDs in camelCase
const PARAMETER_ID_MAPPING: { [key: string]: string } = {
  // Parámetros estándar (camelCase del backend)
  freeChlorine: "freeChlorine",
  combinedChlorine: "combinedChlorine",
  ph: "ph",
  alkalinity: "alkalinity",
  hardness: "hardness",
  stabilizer: "stabilizer",
  salt: "salt", // Cloro Salino (4-5 ppm)
  salinity: "salinity", // Salinidad Total (2700-3400 ppm)
  phosphates: "phosphates",
  temperature: "temperature",
  orp: "orp",
  tds: "tds",
  // Scuba3s mappings
  scuba_alkalinity: "scuba_alkalinity",
  scuba_bromine: "scuba_bromine",
  scuba_chlorine: "scuba_chlorine",
  scuba_copper: "scuba_copper",
  scuba_cya: "scuba_cya",
  scuba_hardness: "scuba_hardness",
  scuba_phosphate: "scuba_phosphate",
  scuba_oxygen: "scuba_oxygen",
  scuba_ph: "scuba_ph",
};

function calculateStatus(
  value: number,
  optimalMin: number,
  optimalMax: number,
  warningMin: number,
  warningMax: number,
): ParameterStatus {
  if (value >= optimalMin && value <= optimalMax) {
    return "optimal";
  }
  if (value >= warningMin && value <= warningMax) {
    return "warning";
  }
  return "critical";
}

export function usePoolData(): UsePoolDataReturn {
  const [pools, setPools] = useState<Pool[]>([]);
  const [selectedPool, setSelectedPool] = useState<Pool | null>(null);
  const [parameters, setParameters] = useState<PoolParameterWithValue[]>([]);
  const [latestMeasurement, setLatestMeasurement] =
    useState<Measurement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load pools on mount
  useEffect(() => {
    loadPools();
  }, []);

  // Load parameters and measurements when pool is selected
  useEffect(() => {
    if (selectedPool) {
      loadPoolData(selectedPool.id);
    }
  }, [selectedPool]);

  const loadPools = async () => {
    try {
      setLoading(true);
      const poolList = await poolApi.getAll();
      setPools(poolList);

      // Auto-select first pool
      if (poolList.length > 0) {
        setSelectedPool(poolList[0]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error loading pools");
    } finally {
      setLoading(false);
    }
  };

  const loadPoolData = async (poolId: number) => {
    try {
      setLoading(true);
      setError(null);

      // Load parameters and latest measurement in parallel
      const [params, measurement] = await Promise.all([
        parameterApi.getAll(),
        // Gracefully handle 404 when no measurements exist (new system/empty database)
        measurementApi.getLatestByPoolId(poolId).catch((err) => {
          // 404 is expected when no measurements exist - not an error
          if (err instanceof Error && err.message.includes("404")) {
            return null;
          }
          // Re-throw other errors
          throw err;
        }),
      ]);

      // Debug: Log raw API responses
      console.log("🔍 Pool Data Debug:", {
        poolId,
        paramsCount: params.length,
        measurement: measurement
          ? {
              id: measurement.id,
              timestamp: measurement.timestamp,
              readingsCount: measurement.readings?.length || 0,
              readings: measurement.readings,
            }
          : "No measurement found",
      });

      // Combine parameters with measurement values
      const parametersWithValues: PoolParameterWithValue[] = params.map(
        (param) => {
          // Map API parameter ID to measurement reading ID
          const measurementParameterId =
            PARAMETER_ID_MAPPING[param.parameterId] || param.parameterId;

          const reading = measurement?.readings?.find(
            (r) => r.parameterId === measurementParameterId,
          );

          const currentValue = reading?.value ?? null;
          const status: ParameterStatus =
            currentValue !== null
              ? calculateStatus(
                  currentValue,
                  param.optimalMin,
                  param.optimalMax,
                  param.warningMin,
                  param.warningMax,
                )
              : "optimal";

          console.log(
            `📊 Parameter: ${param.name} (${param.parameterId} -> ${measurementParameterId})`,
            {
              currentValue,
              status,
              reading,
            },
          );

          return {
            ...param,
            currentValue,
            status,
          };
        },
      );

      // Debug: Summary of parameters with/without values
      const withValues = parametersWithValues.filter(
        (p) => p.currentValue !== null,
      );
      const withoutValues = parametersWithValues.filter(
        (p) => p.currentValue === null,
      );
      console.log("📈 Parameters Summary:", {
        total: parametersWithValues.length,
        withValues: withValues.length,
        withoutValues: withoutValues.length,
        parameterIds: parametersWithValues.map((p) => p.parameterId),
        readingIds: measurement?.readings?.map((r) => r.parameterId) || [],
      });

      setParameters(parametersWithValues);
      setLatestMeasurement(measurement);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error loading pool data");
    } finally {
      setLoading(false);
    }
  };

  const selectPool = useCallback(
    (poolId: number) => {
      const pool = pools.find((p) => p.id === poolId);
      if (pool) {
        setSelectedPool(pool);
      }
    },
    [pools],
  );

  const refresh = useCallback(() => {
    if (selectedPool) {
      loadPoolData(selectedPool.id);
    }
  }, [selectedPool]);

  return {
    pools,
    selectedPool,
    parameters,
    latestMeasurement,
    loading,
    error,
    selectPool,
    refresh,
  };
}
